import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {CurrencyRate, ReferenceBookResponse, ReferenceBookType, RefundResponse, RefundType, TransactionType} from '@npck/af-frontend-api';
import {Spinner} from '@npck/libs/commons';
import {KZT_CURRENCY_CODE, REFUND_TRANSFER_TYPE_CODES, TRANSFER_CATEGORY_CODE} from 'projects/af/src/app/domain/constants';
import {combineLatest} from 'rxjs';
import {DictionarySelectorComponent} from '../../../../components/dictionary/dictionary-selector.component';
import {CurrencyRatesHolderService} from '../../../../domain/currency-rates-holder.service';
import {DictionaryHolderService, findValueInDictionary} from '../../../../domain/dictionary-holder.service';
import {
    requiredFormGroupValidator,
} from '../../../incidents/create-edit/transaction-information-step/transcation-information/transaction-information.component';
import {
    ParticipantForm,
    ParticipantViewModel,
} from '../../../incidents/create-edit/transaction-information-step/transcation-information/transaction-participant/transaction-participant.component';

@Component({
    selector: 'af-refund-transaction-step',
    standalone: false,
    templateUrl: './refund-transaction-step.component.html',
})
export class RefundTransactionStepComponent implements OnInit, AfterViewInit {
    @Input() form: FormGroup<RefundTransactionForm>;
    @Input() refund: RefundResponse;
    @Input() sender: ParticipantViewModel;
    @Input() receiver: ParticipantViewModel;
    @Input() incidentId: string;
    @Input() refundType: string;
    @Input() isProcessingBySender: boolean;
    @Input() showDraftButton: boolean;
    @Output() cancel = new EventEmitter<void>();
    @Output() nextStep = new EventEmitter<void>();
    @Output() previousStep = new EventEmitter<void>();
    @ViewChild('typeSelector') typeSelector!: DictionarySelectorComponent;
    @ViewChildren(DictionarySelectorComponent) dictionarySelectors!: QueryList<DictionarySelectorComponent>;
    ReferenceBookType = ReferenceBookType;
    Spinner = Spinner;
    Validators = Validators;
    filterCategoryBy: string[] = ['P', 'C'];
    transactionCategory: string;
    transactionType: string;
    filteredTypesList: ReferenceBookResponse[] = [];
    private currencyRates: CurrencyRate[];
    private transactionCategories: ReferenceBookResponse[] = [];
    private transactionTypes: ReferenceBookResponse[] = [];

    constructor(
        private currencyRatesHolderService: CurrencyRatesHolderService,
        private dictionaryHolderService: DictionaryHolderService,
        private formBuilder: FormBuilder,
    ) {
    }

    ngOnInit(): void {
        combineLatest(
            [
                this.dictionaryHolderService.getReference(ReferenceBookType.TRANSACTION_CATEGORY),
                this.dictionaryHolderService.getReference(ReferenceBookType.TRANSACTION_TYPE),
            ],
        )
            .subscribe(([transactionCategories, transactionTypes]) => {
                this.transactionCategories = transactionCategories;
                this.transactionTypes = transactionTypes;
                this.buildForm();
            });

        this.currencyRatesHolderService.getRates()
            .subscribe(currencyRates => {
                this.currencyRates = currencyRates;
                if (this.form.controls.amountInKzt) {
                    this.recalculateAmountInCurrency();
                }
            });
    }

    submit(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }
        this.nextStep.emit();
    }

    ngAfterViewInit(): void {
        this.syncTypeSelector();
        this.dictionarySelectors.changes.subscribe(() => {
            this.syncTypeSelector();
        });
    }

    private buildForm(): void {
        if (this.refundType === RefundType.PROSECUTOR_ORDER) {
            this.filterCategoryBy = ['P'];
        }
        let category = this.form.controls?.category?.value;

        if (this.refund) {
            const type = findValueInDictionary(
                this.transactionTypes,
                this.refund.transactionType,
                'code1',
            );
            category = findValueInDictionary(this.transactionCategories, type, 'code');
        }

        this.form.addControl('category', new FormControl(category, Validators.required));
        this.form.addControl('type', new FormControl(this.refund?.transactionType, Validators.required));
        this.form.addControl('amount', new FormControl(this.refund?.transactionAmount, Validators.required));
        this.form.addControl('currency', new FormControl(this.refund?.transactionCurrency, Validators.required));
        this.form.addControl('sender', this.formBuilder.group({}, {validators: requiredFormGroupValidator()}));
        this.form.addControl('receiver', this.formBuilder.group({}, {validators: requiredFormGroupValidator()}));

        this.form.controls.amount.valueChanges.subscribe(() => {
            if (this.form.controls.currency.value && this.form.controls.currency.value !== KZT_CURRENCY_CODE) {
                this.recalculateAmountInCurrency();
            }
        });
        this.form.controls.currency.valueChanges.subscribe(value => {
            if (value && value !== KZT_CURRENCY_CODE) {
                if (!this.form.controls.amountInKzt) {
                    this.form.addControl('amountInKzt', new FormControl(this.refund?.transactionAmountInKzt, Validators.required));
                }
                this.recalculateAmountInCurrency();
            } else {
                this.form.removeControl('amountInKzt');
            }
        });

        this.updateFilteredTypes(this.form.controls.category.value);
        this.form.controls.category.valueChanges.subscribe(categoryValue => {
            this.updateFilteredTypes(categoryValue);
        });
    }

    private updateFilteredTypes(category: string): void {
        if (!this.transactionTypes || this.transactionTypes.length === 0) {
            this.filteredTypesList = [];
            return;
        }
        let newList: ReferenceBookResponse[] = [];
        if (category === TRANSFER_CATEGORY_CODE) {
            newList = this.transactionTypes.filter(t => REFUND_TRANSFER_TYPE_CODES.includes(t.code as TransactionType));
        } else if (category) {
            newList = this.transactionTypes.filter(t => t.code1 === category);
        }
        this.filteredTypesList = [...newList];
        if (this.typeSelector) {
            this.typeSelector.filteredDictionary = this.filteredTypesList;
            this.typeSelector.selectedItems = [];
            this.typeSelector.updateDisplayedValue();
        }
        this.syncTypeSelector();
    }

    private syncTypeSelector(): void {
        if (!this.dictionarySelectors || this.dictionarySelectors.length === 0) {
            return;
        }
        const typeSelector = this.dictionarySelectors.find(s => s.control === this.form.controls.type);
        if (typeSelector) {
            typeSelector.filteredDictionary = [...this.filteredTypesList];
            const currentValue = this.form.controls.type.value ?? this.refund?.transactionType;
            const match = this.filteredTypesList.find(item => item.code === currentValue || item.name_ru === currentValue);
            if (match) {
                typeSelector.selectedItems = [match];
            } else {
                typeSelector.selectedItems = [];
            }
            typeSelector.updateDisplayedValue();
        }
    }

    private recalculateAmountInCurrency(): void {
        const rateItem = this.currencyRates?.find(rate => rate.code === this.form.getRawValue().currency)?.oneUnitRate;
        if (!rateItem) {
            return;
        }
        const amountInKzt = this.form.getRawValue().amount * rateItem;
        this.form.controls.amountInKzt.setValue(amountInKzt);
    }
}

export interface RefundTransactionForm {
    category?: FormControl<string>;
    type?: FormControl<string>;
    amount?: FormControl<number>;
    amountInKzt?: FormControl<number>;
    currency?: FormControl<string>;
    sender?: FormGroup<ParticipantForm>;
    receiver?: FormGroup<ParticipantForm>;
}
